import React from 'react';
import { isEqual, keys } from 'lodash';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';

import styles from './ProgressButtons.module.scss';
import Button from '../../Button/Button';

import {
  stepNavigation,
  DESTINATION_STEP,
  DISTRIBUTION_METHOD_STEP,
  CUSTOM_DISTRIBUTION_METHOD_STEP,
  PROPORTIONATE_DISTRIBUTION_METHOD_STEP,
  REINVEST_STEP,
  FREQUENCY_STEP,
  TAX_WITHHOLDING_STEP,
  BANK_STEP,
  REVIEW_SUBMIT_STEP,
  stepNavigationEdit,
  REINVEST_TO_NEW,
  FEDERAL_WITHOLDINGS,
} from '../../../settings/settings';
import {
  getSelectedDestination,
  getSelectedBank,
  getSelectedReinvestment,
  getSelectedDistribution,
  getSelectedDistributionFunds,
  getSelectedDay,
  getSelectedMonths,
  getTaxWithholding,
} from '../../../redux/modules/shoppingCart/shoppingCart.selectors';
import { moduleActions } from '../../../redux/modules/index';
import {
  openModal as openModalAction,
  CONFIRM_MODAL,
  FAILURE_MODAL,
  SUCCESS_MODAL,
  SELECTED_DATE_MODAL,
} from '../../../redux/modules/modals/modals.module';
import {
  getOnlyOWNRRMDs,
  getRMDfunds,
  getSubmitProportionalRMDFunds,
} from '../../../redux/modules/UserFunds/userFunds.selectors';
import { setCurrentStep } from '../../../redux/modules/applicationData/applicationData.module';

const ProgressButtons = ({
  showBackButton = true,
  backDisabled = false,
  disabled,
  currentStep,
  previousStep,
  currentSelection,
  payload,
  nextStep,
  children,
  edit,
  isWithholdOptionChanged,
  ...props
}) => {
  const dispatch = useDispatch();
  const destination = useSelector(getSelectedDestination);
  const destinationBank = useSelector(getSelectedBank);
  const destinationReinvest = useSelector(getSelectedReinvestment).fund;
  const distribution = useSelector(getSelectedDistribution);
  const distributionFunds = useSelector(getSelectedDistributionFunds);
  const allProportionalFund = useSelector(getSubmitProportionalRMDFunds);
  const frequencyMonths = useSelector(getSelectedMonths);
  const frequencyDay = useSelector(getSelectedDay);
  const withHolding = useSelector(getTaxWithholding);
  const hasExistingRMD = useSelector((state) => getOnlyOWNRRMDs(state));
  const isTaxPayerSelected = useSelector(
    (state) => state.shoppingCart.isTaxPayerSelected
  );
  const {
    ROLLOVER_IRA: { unlockedFunds: rolloverUnlockedFunds },
    TRADITIONAL_IRA: { unlockedFunds: traditionalUnlockedFunds },
  } = useSelector(getRMDfunds);
  const changesMade = useSelector((state) => state.shoppingCart.changesMade);
  const withholdingChanged = useSelector(
    (state) => state.shoppingCart.withholdingChanged
  );
  function updateState() {
    dispatch(moduleActions.editExistingRMD());
    switch (currentStep) {
      // Step 1
      case `${DESTINATION_STEP}`:
        dispatch(moduleActions.updateDestinationType(payload));
        if (payload !== currentSelection) {
          dispatch(moduleActions.updateSelectedBank({}));
          dispatch(moduleActions.updateSelectedReinvestment({}));
          dispatch(moduleActions.updateSelectedAddress({}));
          dispatch(moduleActions.updateChangesMade({}));
          dispatch(moduleActions.updateSelectedDistributionFunds({}));
          dispatch(moduleActions.updateSelectedDistributionType('none'));
        }
        break;
      case `${BANK_STEP}`:
        dispatch(moduleActions.updateSelectedBank(payload));
        break;
      // Step 2
      case `${DISTRIBUTION_METHOD_STEP}`:
        if (payload) {
          dispatch(moduleActions.updateSelectedDistributionType(payload));
          if (payload !== currentSelection) {
            dispatch(moduleActions.updateSelectedDistributionFunds({}));
            dispatch(moduleActions.updateSelectedReinvestment({}));
            dispatch(moduleActions.updateChangesMade({}));
          }
        } else {
          dispatch(
            moduleActions.updateSelectedDistributionFunds({
              rolloverFunds: rolloverUnlockedFunds,
              traditionalFunds: traditionalUnlockedFunds,
            })
          );
        }
        break;
      case `${CUSTOM_DISTRIBUTION_METHOD_STEP}`:
        dispatch(moduleActions.updateSelectedDistributionFunds(payload));
        if (payload.rolloverFunds && keys(payload.rolloverFunds).length > 0) {
          dispatch(
            moduleActions.searchExchangeToExistingRollover(
              payload.rolloverFunds[keys(payload.rolloverFunds)[0]]
            )
          );
        }
        if (
          payload.traditionalFunds &&
          keys(payload.traditionalFunds).length > 0
        ) {
          dispatch(
            moduleActions.searchExchangeToExistingTraditional(
              payload.traditionalFunds[keys(payload.traditionalFunds)[0]]
            )
          );
        }
        if (!isEqual(payload, currentSelection)) {
          dispatch(moduleActions.updateSelectedReinvestment({}));
          dispatch(moduleActions.updateChangesMade({}));
        }
        break;
      case `${PROPORTIONATE_DISTRIBUTION_METHOD_STEP}`:
        dispatch(moduleActions.updateSelectedDistributionFunds(payload));
        if (payload.rolloverFunds && payload.rolloverFunds.length > 0) {
          dispatch(
            moduleActions.searchExchangeToExistingRollover(
              payload.rolloverFunds[0]
            )
          );
        }
        if (payload.traditionalFunds && payload.traditionalFunds.length > 0) {
          dispatch(
            moduleActions.searchExchangeToExistingTraditional(
              payload.traditionalFunds[0]
            )
          );
        }
        break;
      case `${REINVEST_STEP}`:
        if (!isEqual(payload, currentSelection)) {
          dispatch(moduleActions.updateSelectedReinvestment(payload));
          dispatch(moduleActions.updateChangesMade({}));
        }
        break;
      case `${REINVEST_TO_NEW}`:
        if (!isEqual(payload, currentSelection)) {
          dispatch(moduleActions.updateSelectedReinvestment(payload));
          dispatch(moduleActions.updateChangesMade({}));
        }
        break;
      // Step 3
      case `${FREQUENCY_STEP}`:
        const reqBody = {
          systematicsSelections: [
            {
              agreementID: '1',
              days: payload.day.map((day) => day.value),
              months: payload.months.map((month) => month.value),
            },
          ],
        };
        if (!isEqual(payload, currentSelection)) {
          dispatch(moduleActions.getEstimatedDate(reqBody));
          dispatch(moduleActions.updateSelectedFrequency(payload));
          dispatch(moduleActions.updateChangesMade({}));
        }
        break;
      // Step 4
      case `${TAX_WITHHOLDING_STEP}`:
        dispatch(moduleActions.updateSelectedTaxWithholding(payload));

        let hasChanged =
          payload.selected !== currentSelection.selected ||
          payload.value !== currentSelection.value;

        if (!currentSelection?.selected) {
          hasChanged =
            payload.selected !== FEDERAL_WITHOLDINGS.ELECT_FEDERAL_WITHHOLDING;
        }
        dispatch(
          moduleActions.updateWithholdingMade({
            withholdingChanged: !!hasChanged,
          })
        );
        break;
      default:
    }
  }

  function goForward(props) {
    updateState();
    if (props?.isSelectedPastDate) {
      dispatch(openModalAction(SELECTED_DATE_MODAL));
      return;
    }
    dispatch(setCurrentStep(nextStep));
    if (edit) {
      props.history.push(stepNavigationEdit[nextStep].step);
    } else {
      props.history.push(stepNavigation[nextStep].step);
    }
  }

  function goBack(props) {
    updateState();
    dispatch(setCurrentStep(previousStep));
    if (edit) {
      props.history.push(stepNavigationEdit[previousStep].step);
    } else {
      props.history.push(stepNavigation[previousStep].step);
    }
  }

  function submit() {
    const rolloverFunds = distributionFunds.rolloverFunds
      ? Object.values(distributionFunds.rolloverFunds)
      : [];
    const traditionalFunds = distributionFunds.traditionalFunds
      ? Object.values(distributionFunds.traditionalFunds)
      : [];
    const allFunds =
      distribution === PROPORTIONATE_DISTRIBUTION_METHOD_STEP
        ? allProportionalFund
        : rolloverFunds.concat(traditionalFunds);
    const delivery =
      destination === BANK_STEP
        ? 'ACH_TO_BANK'
        : destination === REINVEST_STEP
        ? 'EXCHANGE'
        : destination === REINVEST_TO_NEW
        ? 'EXCHANGE_TO_NEW'
        : 'CHECK_TO_ADDRESS';

    let hasRMD = false;
    if (hasExistingRMD && hasExistingRMD.length > 0) {
      hasRMD = true;
    }
    let payload = {
      rmdAgreements: allFunds
        .filter((fund) => {
          return (
            distribution === PROPORTIONATE_DISTRIBUTION_METHOD_STEP ||
            fund.value > 0
          );
        })
        .map((fund) => {
          return {
            agreementId: parseInt(fund.agreementID),
            rmdPercentage: fund.value ? fund.value : 0,
            delivery: delivery,
            federal_tax_withholding_percentage: withHolding.value
              ? withHolding.value
              : 0,

            targetAgreementFundNumber:
              destination === REINVEST_TO_NEW
                ? destinationReinvest.rkpCode
                : null,
            targetAgreementId:
              destinationReinvest && destination !== REINVEST_TO_NEW
                ? parseInt(destinationReinvest.agreementID)
                : null,
          };
        }),
      iraFedralWitholding: withHolding.value ? withHolding.value : 0,
      activeIndicator: true,
      withdrawMonthText: frequencyMonths.map((month) => month.txtVal),
      withdrawDayNumber: frequencyDay[0].value,
      rmdTypeCode:
        distribution === PROPORTIONATE_DISTRIBUTION_METHOD_STEP
          ? 'Proportionate'
          : 'Disproportionate',
      rmdPlanTypeId: 250,
      rmdScheduleStatus: 'CREATE',
      bankDto: {
        name: destinationBank.name,
        bankAgreementID: destinationBank.bankAgreementID
          ? destinationBank.bankAgreementID
          : '',
        sameDayBank: false,
      },
      withholdingUpdateOnly: withholdingChanged,
      eligibleForTaxWithholding: isTaxPayerSelected,
    };
    if (destination === REINVEST_TO_NEW) {
      payload = {
        ...payload,
        ...{
          reinvestToNew: {
            newAccount: true,
            backupWithhold: isTaxPayerSelected,
            accountType: 'INDIVIDUAL',
          },
        },
      };
    }
    if (!changesMade && !withholdingChanged) {
      dispatch(openModalAction(CONFIRM_MODAL, {}));
    } else if (hasRMD) {
      payload.rmdScheduleStatus = 'MODIFY';
      Promise.all([dispatch(moduleActions.editExisting(payload))]).then(
        () => {
          dispatch(openModalAction(SUCCESS_MODAL, { isEdit: true }));
        },
        () => {
          dispatch(openModalAction(FAILURE_MODAL, {}));
        }
      );
    } else {
      Promise.all([dispatch(moduleActions.submitNewRMD(payload))]).then(
        () => {
          dispatch(openModalAction(SUCCESS_MODAL));
        },
        () => {
          dispatch(openModalAction(FAILURE_MODAL, {}));
        }
      );
    }
  }

  return (
    <div className={styles.container}>
      {!showBackButton ? (
        <span style={{ width: `50px` }} />
      ) : (
        <Button
          id="go-back"
          disabled={backDisabled}
          onClick={() => goBack(props)}
          type="secondary"
        >
          <img
            src={require('../../../assets/images/leftArrow.svg')}
            alt=""
            className={styles['left-arrow']}
          />
          Back
        </Button>
      )}
      {children}
      <Button
        id="go-forward"
        disabled={disabled}
        type="primary"
        onClick={() => {
          currentStep === REVIEW_SUBMIT_STEP ? submit() : goForward(props);
        }}
      >
        {currentStep === REVIEW_SUBMIT_STEP ? 'Submit' : 'Continue'}
      </Button>
    </div>
  );
};

export default compose(withRouter)(ProgressButtons);
